import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Paper,
  Card,
  CardContent,
  alpha,
  useTheme,
  Tooltip,
  Stack,
} from '@mui/material';
import {
  Close as CloseIcon,
  AccountBalance as AccountBalanceIcon,
  Person as PersonIcon,
  Receipt as ReceiptIcon,
  LocalShipping as LocalShippingIcon,
  Store as StoreIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AccountTree as AccountTreeIcon,
  LockOpen as LockOpenIcon,
  AddCircle as ReplenishIcon,
  Description as SettlementIcon,
  Summarize as SummaryIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { Custody } from '../../types/custodies.types';
import { custodiesService } from '../../services/custodies.service';
import { useLanguage } from '../../contexts/LanguageContext';
import { useConfirm } from '../../hooks/useConfirm';

interface CustodyDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  custody: Custody | null;
  onEdit?: (custody: Custody) => void;
  onDeleted?: () => void;
}

const translations = {
  en: {
    title: 'Custody Details',
    custodyInfo: 'Custody Information',
    custodyNumber: 'Custody Number',
    custodyDate: 'Custody Date',
    employee: 'Employee',
    branch: 'Branch',
    account: 'Custody Account',
    initialAmount: 'Initial Amount',
    currentBalance: 'Current Balance',
    status: 'Status',
    notes: 'Notes',
    purchases: 'Purchases',
    warehouseDeliveries: 'Warehouse Deliveries',
    branchDeliveries: 'Branch Deliveries',
    invoiceNumber: 'Invoice Number',
    supplier: 'Supplier',
    amount: 'Amount',
    warehouse: 'Warehouse',
    deliveryDate: 'Delivery Date',
    totalValue: 'Total Value',
    close: 'Close',
    edit: 'Edit',
    delete: 'Delete',
    reopen: 'Reopen',
    noPurchases: 'No purchases',
    noWarehouseDeliveries: 'No warehouse deliveries',
    noBranchDeliveries: 'No branch deliveries',
    noAccount: 'No account linked',
    purchaseDate: 'Purchase Date',
    totalPurchases: 'Total Purchases',
    totalWarehouseDeliveries: 'Total Warehouse Deliveries',
    totalBranchDeliveries: 'Total Branch Deliveries',
    deleteConfirm: 'Are you sure you want to delete this custody? This action cannot be undone.',
    reopenConfirm: 'Are you sure you want to reopen this custody?',
    deleteSuccess: 'Custody deleted successfully',
    reopenSuccess: 'Custody reopened successfully',
    deleteFailed: 'Failed to delete custody',
    reopenFailed: 'Failed to reopen custody',
    cannotDelete: 'Cannot delete custody with linked operations',
    kwd: 'KWD',
    replenishments: 'Replenishments (Top-ups)',
    noReplenishments: 'No replenishments',
    totalReplenishments: 'Total Replenishments',
    replenishmentDate: 'Date',
    paymentMethod: 'Payment Method',
    referenceNumber: 'Reference',
    settlements: 'Expense Settlements',
    noSettlements: 'No settlements',
    totalSettlements: 'Total Settlement Expenses',
    settlementNumber: 'Settlement #',
    settlementDate: 'Date',
    expenses: 'Expenses',
    returned: 'Returned',
    financialSummary: 'Financial Summary',
    grandTotal: 'Grand Total (Initial + Top-ups)',
    totalExpenses: 'Total Expenses',
    netBalance: 'Net Balance',
  },
  ar: {
    title: 'تفاصيل العهدة',
    custodyInfo: 'معلومات العهدة',
    custodyNumber: 'رقم العهدة',
    custodyDate: 'تاريخ العهدة',
    employee: 'الموظف',
    branch: 'الفرع',
    account: 'حساب العهدة',
    initialAmount: 'المبلغ الأولي',
    currentBalance: 'الرصيد الحالي',
    status: 'الحالة',
    notes: 'ملاحظات',
    purchases: 'المشتريات',
    warehouseDeliveries: 'التوريدات للمخازن',
    branchDeliveries: 'التوريدات للفروع',
    invoiceNumber: 'رقم الفاتورة',
    supplier: 'المورد',
    amount: 'المبلغ',
    warehouse: 'المخزن',
    deliveryDate: 'تاريخ التوريد',
    totalValue: 'القيمة الإجمالية',
    close: 'إغلاق',
    edit: 'تعديل',
    delete: 'حذف',
    reopen: 'إعادة فتح',
    noPurchases: 'لا توجد مشتريات',
    noWarehouseDeliveries: 'لا توجد توريدات للمخازن',
    noBranchDeliveries: 'لا توجد توريدات للفروع',
    noAccount: 'غير مرتبط بحساب',
    purchaseDate: 'تاريخ الشراء',
    totalPurchases: 'إجمالي المشتريات',
    totalWarehouseDeliveries: 'إجمالي التوريدات للمخازن',
    totalBranchDeliveries: 'إجمالي التوريدات للفروع',
    deleteConfirm: 'هل أنت متأكد من حذف هذه العهدة؟ لا يمكن التراجع عن هذا الإجراء.',
    reopenConfirm: 'هل أنت متأكد من إعادة فتح هذه العهدة؟',
    deleteSuccess: 'تم حذف العهدة بنجاح',
    reopenSuccess: 'تم إعادة فتح العهدة بنجاح',
    deleteFailed: 'فشل حذف العهدة',
    reopenFailed: 'فشل إعادة فتح العهدة',
    cannotDelete: 'لا يمكن حذف عهدة تحتوي على عمليات مرتبطة',
    kwd: 'د.ك',
    replenishments: 'تعزيزات العهدة (الزيادات)',
    noReplenishments: 'لا توجد تعزيزات',
    totalReplenishments: 'إجمالي التعزيزات',
    replenishmentDate: 'التاريخ',
    paymentMethod: 'طريقة الدفع',
    referenceNumber: 'المرجع',
    settlements: 'كشوف تسوية المصاريف',
    noSettlements: 'لا توجد تسويات',
    totalSettlements: 'إجمالي مصاريف التسويات',
    settlementNumber: 'رقم التسوية',
    settlementDate: 'التاريخ',
    expenses: 'المصاريف',
    returned: 'المسترد',
    financialSummary: 'الملخص المالي',
    grandTotal: 'الإجمالي الكلي (أولي + زيادات)',
    totalExpenses: 'إجمالي المصاريف',
    netBalance: 'الرصيد الصافي',
  },
};

const CustodyDetailsDialog: React.FC<CustodyDetailsDialogProps> = ({ 
  open, 
  onClose, 
  custody,
  onEdit,
  onDeleted,
}) => {
  const { language } = useLanguage();
  const t = translations[language];
  const isRTL = language === 'ar';
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const confirm = useConfirm();

  if (!custody) return null;

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'ar' ? 'ar-KW' : 'en-GB');
  };

  const formatCurrency = (amount: number) => {
    return `${amount.toFixed(3)} ${t.kwd}`;
  };

  const totalPurchases = custody.purchases.reduce((sum, p) => sum + p.amount, 0);
  const totalWarehouseDeliveries = custody.warehouseDeliveries.reduce((sum, d) => sum + d.totalValue, 0);
  const totalBranchDeliveries = custody.branchDeliveries.reduce((sum, d) => sum + d.totalValue, 0);

  const canEdit = custody.status === 'Open';
  const canDelete = custody.status === 'Open';
  const canReopen = custody.status === 'Settled';

  const handleEdit = () => {
    if (onEdit) {
      onEdit(custody);
      onClose();
    }
  };

  const handleDelete = async () => {
    if (!(await confirm({ message: t.deleteConfirm, severity: 'error' }))) return;

    try {
      await custodiesService.delete(custody.custodyId);
      toast.success(t.deleteSuccess, {
        position: 'top-center',
        autoClose: 2000,
        style: { fontSize: '16px', fontWeight: 'bold' },
      });
      if (onDeleted) {
        onDeleted();
      }
      onClose();
    } catch (error: any) {
      let errorMessage = t.deleteFailed;
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        }
      }
      
      toast.error(errorMessage, {
        position: 'top-center',
        autoClose: 5000,
        style: { fontSize: '16px', fontWeight: 'bold' },
      });
    }
  };

  const handleReopen = async () => {
    if (!(await confirm({ message: t.reopenConfirm, severity: 'info' }))) return;

    try {
      await custodiesService.reopen(custody.custodyId);
      toast.success(t.reopenSuccess, {
        position: 'top-center',
        autoClose: 2000,
        style: { fontSize: '16px', fontWeight: 'bold' },
      });
      if (onDeleted) {
        onDeleted();
      }
      onClose();
    } catch (error: any) {
      let errorMessage = t.reopenFailed;
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        }
      }
      
      toast.error(errorMessage, {
        position: 'top-center',
        autoClose: 5000,
        style: { fontSize: '16px', fontWeight: 'bold' },
      });
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth 
      dir={isRTL ? 'rtl' : 'ltr'}
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 12px 48px rgba(0,0,0,0.15)',
          bgcolor: isDarkMode ? '#1a1a2e' : 'white',
        }
      }}
    >
      <DialogTitle 
        sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          background: isDarkMode 
            ? 'linear-gradient(135deg, #0f3460 0%, #16213e 100%)' 
            : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          py: 2.5,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at top right, rgba(255,255,255,0.1) 0%, transparent 60%)',
            pointerEvents: 'none',
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, position: 'relative', zIndex: 1 }}>
          <Box sx={{ 
            bgcolor: 'rgba(255,255,255,0.2)', 
            borderRadius: 2, 
            p: 1,
            display: 'flex',
          }}>
            <AccountBalanceIcon sx={{ fontSize: 28 }} />
          </Box>
          <Typography variant="h6" fontWeight="bold">{t.title}</Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: 'white', position: 'relative', zIndex: 1 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 3 }}>
        {/* Custody Information Cards */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 2.5, mb: 3 }}>
          {/* Custody Info Card */}
          <Card 
            elevation={3} 
            sx={{ 
              borderLeft: '4px solid #667eea',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 24px rgba(102, 126, 234, 0.2)',
              }
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <ReceiptIcon sx={{ color: '#667eea' }} />
                <Typography variant="h6" color="#667eea" fontWeight="bold">{t.custodyInfo}</Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box>
                  <Typography variant="caption" color="textSecondary" fontWeight={500}>{t.custodyNumber}</Typography>
                  <Typography variant="body1" fontWeight="bold" color="primary">{custody.custodyNumber}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="textSecondary" fontWeight={500}>{t.custodyDate}</Typography>
                  <Typography variant="body1">{formatDate(custody.custodyDate)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="textSecondary" fontWeight={500}>{t.status}</Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <Chip 
                      label={custody.status} 
                      color={custody.status === 'Open' ? 'info' : custody.status === 'Settled' ? 'success' : 'warning'} 
                      size="small"
                      sx={{ fontWeight: 'bold', borderRadius: 1.5 }}
                    />
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Employee & Branch Card */}
          <Card 
            elevation={3} 
            sx={{ 
              borderLeft: '4px solid #43e97b',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 24px rgba(67, 233, 123, 0.2)',
              }
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <PersonIcon sx={{ color: '#43e97b' }} />
                <Typography variant="h6" color="#43e97b" fontWeight="bold">{t.employee}</Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box>
                  <Typography variant="caption" color="textSecondary" fontWeight={500}>{t.employee}</Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {isRTL ? (custody.employeeNameAr || custody.employeeName) : custody.employeeName}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="textSecondary" fontWeight={500}>{t.branch}</Typography>
                  <Typography variant="body1">
                    {custody.branchName ? (isRTL ? custody.branchNameAr : custody.branchName) : '-'}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Account Card - NEW */}
          <Card 
            elevation={3} 
            sx={{ 
              borderLeft: '4px solid #ffa726',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 24px rgba(255, 167, 38, 0.2)',
              }
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <AccountTreeIcon sx={{ color: '#ffa726' }} />
                <Typography variant="h6" color="#ffa726" fontWeight="bold">{t.account}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="textSecondary" fontWeight={500}>{t.account}</Typography>
                {custody.accountCode ? (
                  <>
                    <Typography variant="body1" fontWeight="bold" color="#ffa726">
                      {custody.accountCode}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                      {custody.accountName}
                    </Typography>
                  </>
                ) : (
                  <Typography variant="body2" color="textSecondary" fontStyle="italic">
                    {t.noAccount}
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>

          {/* Initial Amount Card */}
          <Card 
            elevation={3} 
            sx={{ 
              borderLeft: '4px solid #4facfe',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 24px rgba(79, 172, 254, 0.2)',
              }
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <AccountBalanceIcon sx={{ color: '#4facfe' }} />
                <Typography variant="h6" color="#4facfe" fontWeight="bold">{t.initialAmount}</Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold" color="#4facfe">
                {formatCurrency(custody.initialAmount || 0)}
              </Typography>
            </CardContent>
          </Card>

          {/* Current Balance Card */}
          <Card 
            elevation={3} 
            sx={{ 
              borderLeft: `4px solid ${custody.currentBalance < 0 ? '#f44336' : '#43e97b'}`,
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: custody.currentBalance < 0 
                  ? '0 8px 24px rgba(244, 67, 54, 0.2)'
                  : '0 8px 24px rgba(67, 233, 123, 0.2)',
              }
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <AccountBalanceIcon sx={{ color: custody.currentBalance < 0 ? '#f44336' : '#43e97b' }} />
                <Typography variant="h6" color={custody.currentBalance < 0 ? '#f44336' : '#43e97b'} fontWeight="bold">
                  {t.currentBalance}
                </Typography>
              </Box>
              <Typography 
                variant="h4" 
                fontWeight="bold" 
                color={custody.currentBalance < 0 ? 'error' : 'success'}
              >
                {formatCurrency(custody.currentBalance)}
              </Typography>
            </CardContent>
          </Card>

          {/* Notes Card */}
          {custody.notes && (
            <Card 
              elevation={3} 
              sx={{ 
                gridColumn: '1 / -1',
                borderLeft: '4px solid #9c27b0',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 24px rgba(156, 39, 176, 0.2)',
                }
              }}
            >
              <CardContent>
                <Typography variant="caption" color="textSecondary" fontWeight={500}>{t.notes}</Typography>
                <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>{custody.notes}</Typography>
              </CardContent>
            </Card>
          )}
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Purchases Section */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <ReceiptIcon sx={{ color: '#667eea', fontSize: 28 }} />
            <Typography variant="h6" fontWeight="bold" color="#667eea">{t.purchases}</Typography>
            <Chip 
              label={custody.purchases.length} 
              size="small" 
              sx={{ bgcolor: '#667eea', color: 'white', fontWeight: 'bold', minWidth: 32 }}
            />
          </Box>
          {custody.purchases.length > 0 ? (
            <Paper elevation={2} sx={{ overflow: 'hidden', borderRadius: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: alpha('#667eea', 0.1) }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>{t.invoiceNumber}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>{t.supplier}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>{t.purchaseDate}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="right">{t.amount}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {custody.purchases.map((purchase) => (
                    <TableRow key={purchase.custodyPurchaseId} hover>
                      <TableCell>{purchase.invoiceNumber}</TableCell>
                      <TableCell>{purchase.supplierName}</TableCell>
                      <TableCell>{formatDate(purchase.purchaseDate)}</TableCell>
                      <TableCell align="right">
                        <Typography fontWeight="bold">{formatCurrency(purchase.amount)}</Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ bgcolor: alpha('#667eea', 0.05) }}>
                    <TableCell colSpan={3} sx={{ fontWeight: 'bold' }}>{t.totalPurchases}</TableCell>
                    <TableCell align="right">
                      <Typography fontWeight="bold" color="primary" variant="h6">
                        {formatCurrency(totalPurchases)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Paper>
          ) : (
            <Paper elevation={1} sx={{ p: 3, textAlign: 'center', bgcolor: alpha(theme.palette.background.default, 0.5), borderRadius: 2 }}>
              <Typography variant="body2" color="textSecondary">{t.noPurchases}</Typography>
            </Paper>
          )}
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Warehouse Deliveries Section */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <LocalShippingIcon sx={{ color: '#4facfe', fontSize: 28 }} />
            <Typography variant="h6" fontWeight="bold" color="#4facfe">{t.warehouseDeliveries}</Typography>
            <Chip 
              label={custody.warehouseDeliveries.length} 
              size="small" 
              sx={{ bgcolor: '#4facfe', color: 'white', fontWeight: 'bold', minWidth: 32 }}
            />
          </Box>
          {custody.warehouseDeliveries.length > 0 ? (
            <Paper elevation={2} sx={{ overflow: 'hidden', borderRadius: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: alpha('#4facfe', 0.1) }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>{t.warehouse}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>{t.deliveryDate}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="right">{t.totalValue}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {custody.warehouseDeliveries.map((delivery) => (
                    <TableRow key={delivery.deliveryId} hover>
                      <TableCell>{isRTL ? delivery.warehouseNameAr : delivery.warehouseName}</TableCell>
                      <TableCell>{formatDate(delivery.deliveryDate)}</TableCell>
                      <TableCell align="right">
                        <Typography fontWeight="bold">{formatCurrency(delivery.totalValue)}</Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ bgcolor: alpha('#4facfe', 0.05) }}>
                    <TableCell colSpan={2} sx={{ fontWeight: 'bold' }}>{t.totalWarehouseDeliveries}</TableCell>
                    <TableCell align="right">
                      <Typography fontWeight="bold" color="primary" variant="h6">
                        {formatCurrency(totalWarehouseDeliveries)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Paper>
          ) : (
            <Paper elevation={1} sx={{ p: 3, textAlign: 'center', bgcolor: alpha(theme.palette.background.default, 0.5), borderRadius: 2 }}>
              <Typography variant="body2" color="textSecondary">{t.noWarehouseDeliveries}</Typography>
            </Paper>
          )}
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Branch Deliveries Section */}
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <StoreIcon sx={{ color: '#43e97b', fontSize: 28 }} />
            <Typography variant="h6" fontWeight="bold" color="#43e97b">{t.branchDeliveries}</Typography>
            <Chip 
              label={custody.branchDeliveries.length} 
              size="small" 
              sx={{ bgcolor: '#43e97b', color: 'white', fontWeight: 'bold', minWidth: 32 }}
            />
          </Box>
          {custody.branchDeliveries.length > 0 ? (
            <Paper elevation={2} sx={{ overflow: 'hidden', borderRadius: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: alpha('#43e97b', 0.1) }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>{t.branch}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>{t.deliveryDate}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="right">{t.totalValue}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {custody.branchDeliveries.map((delivery) => (
                    <TableRow key={delivery.deliveryId} hover>
                      <TableCell>{isRTL ? delivery.branchNameAr : delivery.branchName}</TableCell>
                      <TableCell>{formatDate(delivery.deliveryDate)}</TableCell>
                      <TableCell align="right">
                        <Typography fontWeight="bold">{formatCurrency(delivery.totalValue)}</Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ bgcolor: alpha('#43e97b', 0.05) }}>
                    <TableCell colSpan={2} sx={{ fontWeight: 'bold' }}>{t.totalBranchDeliveries}</TableCell>
                    <TableCell align="right">
                      <Typography fontWeight="bold" color="primary" variant="h6">
                        {formatCurrency(totalBranchDeliveries)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Paper>
          ) : (
            <Paper elevation={1} sx={{ p: 3, textAlign: 'center', bgcolor: alpha(theme.palette.background.default, 0.5), borderRadius: 2 }}>
              <Typography variant="body2" color="textSecondary">{t.noBranchDeliveries}</Typography>
            </Paper>
          )}
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Replenishments Section */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <ReplenishIcon sx={{ color: '#00bcd4', fontSize: 28 }} />
            <Typography variant="h6" fontWeight="bold" color="#00bcd4">{t.replenishments}</Typography>
            <Chip label={custody.replenishments?.length || 0} size="small" sx={{ bgcolor: '#00bcd4', color: 'white', fontWeight: 'bold', minWidth: 32 }} />
          </Box>
          {custody.replenishments?.length > 0 ? (
            <Paper elevation={2} sx={{ overflow: 'hidden', borderRadius: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: alpha('#00bcd4', 0.1) }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>{t.replenishmentDate}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>{t.paymentMethod}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>{t.referenceNumber}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>{t.notes}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="right">{t.amount}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {custody.replenishments.map(r => (
                    <TableRow key={r.replenishmentId} hover>
                      <TableCell>{formatDate(r.replenishmentDate)}</TableCell>
                      <TableCell>{r.paymentMethod || '-'}</TableCell>
                      <TableCell>{r.referenceNumber || '-'}</TableCell>
                      <TableCell>{r.notes || '-'}</TableCell>
                      <TableCell align="right"><Typography fontWeight="bold" color="success.main">+{formatCurrency(r.amount)}</Typography></TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ bgcolor: alpha('#00bcd4', 0.05) }}>
                    <TableCell colSpan={4} sx={{ fontWeight: 'bold' }}>{t.totalReplenishments}</TableCell>
                    <TableCell align="right"><Typography fontWeight="bold" color="#00bcd4" variant="h6">{formatCurrency(custody.totalReplenishments || 0)}</Typography></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Paper>
          ) : (
            <Paper elevation={1} sx={{ p: 3, textAlign: 'center', bgcolor: alpha(theme.palette.background.default, 0.5), borderRadius: 2 }}>
              <Typography variant="body2" color="textSecondary">{t.noReplenishments}</Typography>
            </Paper>
          )}
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Settlements Section */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <SettlementIcon sx={{ color: '#ff7043', fontSize: 28 }} />
            <Typography variant="h6" fontWeight="bold" color="#ff7043">{t.settlements}</Typography>
            <Chip label={custody.settlements?.length || 0} size="small" sx={{ bgcolor: '#ff7043', color: 'white', fontWeight: 'bold', minWidth: 32 }} />
          </Box>
          {custody.settlements?.length > 0 ? (
            <Paper elevation={2} sx={{ overflow: 'hidden', borderRadius: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: alpha('#ff7043', 0.1) }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>{t.settlementNumber}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>{t.settlementDate}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>{t.status}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="right">{t.expenses}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="right">{t.returned}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {custody.settlements.map(s => (
                    <TableRow key={s.settlementId} hover>
                      <TableCell>{s.settlementNumber}</TableCell>
                      <TableCell>{formatDate(s.settlementDate)}</TableCell>
                      <TableCell>
                        <Chip label={s.status} size="small" color={s.status === 'Approved' ? 'success' : s.status === 'Posted' ? 'info' : 'warning'} sx={{ fontWeight: 'bold' }} />
                      </TableCell>
                      <TableCell align="right"><Typography fontWeight="bold" color="error.main">-{formatCurrency(s.totalExpenses)}</Typography></TableCell>
                      <TableCell align="right">{s.returnedAmount > 0 ? formatCurrency(s.returnedAmount) : '-'}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ bgcolor: alpha('#ff7043', 0.05) }}>
                    <TableCell colSpan={3} sx={{ fontWeight: 'bold' }}>{t.totalSettlements}</TableCell>
                    <TableCell align="right"><Typography fontWeight="bold" color="error" variant="h6">{formatCurrency(custody.totalSettlementExpenses || 0)}</Typography></TableCell>
                    <TableCell align="right"><Typography fontWeight="bold">{formatCurrency(custody.totalSettlementReturns || 0)}</Typography></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Paper>
          ) : (
            <Paper elevation={1} sx={{ p: 3, textAlign: 'center', bgcolor: alpha(theme.palette.background.default, 0.5), borderRadius: 2 }}>
              <Typography variant="body2" color="textSecondary">{t.noSettlements}</Typography>
            </Paper>
          )}
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Financial Summary */}
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <SummaryIcon sx={{ color: '#9c27b0', fontSize: 28 }} />
            <Typography variant="h6" fontWeight="bold" color="#9c27b0">{t.financialSummary}</Typography>
          </Box>
          <Paper elevation={3} sx={{ p: 3, borderRadius: 2, border: '2px solid', borderColor: '#9c27b0' }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 3 }}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="caption" color="textSecondary">{t.initialAmount}</Typography>
                <Typography variant="h5" fontWeight="bold" color="#4facfe">{formatCurrency(custody.initialAmount || 0)}</Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="caption" color="textSecondary">{t.totalReplenishments}</Typography>
                <Typography variant="h5" fontWeight="bold" color="#00bcd4">+{formatCurrency(custody.totalReplenishments || 0)}</Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="caption" color="textSecondary">{t.grandTotal}</Typography>
                <Typography variant="h5" fontWeight="bold" color="#9c27b0">{formatCurrency(custody.grandTotal || custody.initialAmount || 0)}</Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="caption" color="textSecondary">{t.totalExpenses}</Typography>
                <Typography variant="h5" fontWeight="bold" color="error">-{formatCurrency((custody.totalSettlementExpenses || 0) + totalPurchases + totalWarehouseDeliveries + totalBranchDeliveries)}</Typography>
              </Box>
              <Box sx={{ textAlign: 'center', gridColumn: '1 / -1' }}>
                <Typography variant="caption" color="textSecondary">{t.netBalance}</Typography>
                <Typography variant="h3" fontWeight="bold" color={custody.currentBalance < 0 ? 'error' : 'success.main'}>
                  {formatCurrency(custody.currentBalance)}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Box>
      </DialogContent>

      <DialogActions 
        sx={{ 
          px: 3, 
          py: 2.5,
          bgcolor: isDarkMode ? alpha(theme.palette.background.paper, 0.5) : '#f5f5f5',
          gap: 1.5,
        }}
      >
        <Box sx={{ flex: 1 }}>
          {canDelete && (
            <Tooltip title={t.delete} arrow>
              <Button
                onClick={handleDelete}
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                size="large"
                sx={{
                  borderRadius: 2,
                  px: 3,
                  borderWidth: 2,
                  '&:hover': {
                    borderWidth: 2,
                    bgcolor: alpha(theme.palette.error.main, 0.1),
                  }
                }}
              >
                {t.delete}
              </Button>
            </Tooltip>
          )}
        </Box>
        <Stack direction="row" spacing={1.5}>
          {canReopen && (
            <Button
              onClick={handleReopen}
              variant="outlined"
              color="warning"
              startIcon={<LockOpenIcon />}
              size="large"
              sx={{
                borderRadius: 2,
                px: 3,
                borderWidth: 2,
                '&:hover': {
                  borderWidth: 2,
                  bgcolor: alpha(theme.palette.warning.main, 0.1),
                }
              }}
            >
              {t.reopen}
            </Button>
          )}
          {canEdit && onEdit && (
            <Button
              onClick={handleEdit}
              variant="outlined"
              color="info"
              startIcon={<EditIcon />}
              size="large"
              sx={{
                borderRadius: 2,
                px: 3,
                borderWidth: 2,
                '&:hover': {
                  borderWidth: 2,
                  bgcolor: alpha(theme.palette.info.main, 0.1),
                }
              }}
            >
              {t.edit}
            </Button>
          )}
          <Button 
            onClick={onClose} 
            variant="contained"
            size="large"
            sx={{
              borderRadius: 2,
              px: 4,
              background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
              boxShadow: '0 3px 5px 2px rgba(102, 126, 234, .3)',
              '&:hover': { 
                background: 'linear-gradient(45deg, #5568d3 30%, #653d8a 90%)',
                boxShadow: '0 5px 8px 3px rgba(102, 126, 234, .4)',
              }
            }}
          >
            {t.close}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default CustodyDetailsDialog;